import { type FC, useContext, useState } from "react";

import { Auth } from "@aws-amplify/auth";
import { joiResolver } from "@hookform/resolvers/joi";
import Joi from "joi";
import { useForm } from "react-hook-form";

import {
  Button,
  Icon,
  Input,
  toastError,
  toastSuccess,
} from "@alpacahq/ui/old";

import { initAmplify } from "~/v2/api/auth/config";
import { AuthContext, AuthState } from "../AuthContext";

const PASSWORD_RESET_SCHEMA = Joi.object({
  verificationCode: Joi.string().required().messages({
    "string.empty": "Verification code is required",
  }),
  newPassword: Joi.string()
    .required()
    .min(12)
    .pattern(/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])/)
    .messages({
      "string.empty": "New password is required",
      "string.min": "Password must contain at least 12 characters",
      "string.pattern.base":
        "Password must contain both uppercase and lowercase letters, at least one number or symbol",
    }),
  confirmPassword: Joi.string()
    .valid(Joi.ref("newPassword"))
    .required()
    .messages({
      "string.empty": "Confirmation of the new password is required",
      "any.only": "New password and confirmation must match",
    }),
}).with("newPassword", "confirmPassword");

const EMAIL_VALIDATION_SCHEMA = Joi.object({
  email: Joi.string()
    .email({ tlds: { allow: false } })
    .required()
    .messages({
      "string.empty": "Please enter an email",
      "string.email": "Must be a valid email",
    }),
});

type EmailFields = {
  email: string;
};

type ResetFields = {
  verificationCode: string;
  newPassword: string;
  confirmPassword: string;
};

export const ForgotPassword: FC = () => {
  const { setAuthState } = useContext(AuthContext);

  const [email, setEmail] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [lastResend, setLastResend] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [status, setStatus] = useState<"neutral" | "error" | "success">(
    "neutral",
  );

  const {
    register: registerReset,
    handleSubmit: handleSubmitReset,
    formState: { errors: errorsReset },
    reset: resetResetForm,
  } = useForm<ResetFields>({
    mode: "onBlur",
    reValidateMode: "onChange",
    resolver: joiResolver(PASSWORD_RESET_SCHEMA),
  });

  const {
    register: registerEmail,
    handleSubmit: handleSubmitEmail,
    formState: { errors: errorsEmail },
  } = useForm<EmailFields>({
    mode: "onBlur",
    reValidateMode: "onChange",
    resolver: joiResolver(EMAIL_VALIDATION_SCHEMA),
  });

  const handleCancel = () => setAuthState(AuthState.LOGIN);

  const sendResetCode = async (data: EmailFields) => {
    setIsSubmitting(true);
    initAmplify();

    try {
      // send the password reset request
      await Auth.forgotPassword(data.email.toLowerCase());

      setCodeSent(true);
      setEmail(data.email.toLowerCase());

      toastSuccess("Password reset code has been sent to your email");
    } catch (err) {
      const error = err as Error;

      setStatus("error");

      toastError(
        error.message || "An error occurred during password reset request",
        {},
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetPassword = async (data: ResetFields) => {
    setIsSubmitting(true);
    initAmplify();

    try {
      await Auth.forgotPasswordSubmit(
        email.toLowerCase(),
        data.verificationCode,
        data.newPassword,
      );

      toastSuccess("Password reset successful!");

      // highlight the inputs green
      setStatus("success");

      // sleep for 1.5 seconds to allow the success toast to show
      await new Promise((r) => setTimeout(r, 1500));

      // redirect
      setAuthState(AuthState.LOGIN);
    } catch (err) {
      const error = err as Error;

      setStatus("error");
      toastError(error.message || "An error occurred during password reset");
    } finally {
      resetResetForm();
      setIsSubmitting(false);
    }
  };

  const resendCode = async () => {
    // get current timestamp
    const now = Date.now();

    // calculate milliseconds since the last resend
    const msSinceLastResend = now - lastResend;

    // set a throttle limit of 5 seconds to prevent spamming
    const throttleLimit = 5000;

    // if the time since the last resend is less than the throttle limit, show an error toast and exit
    if (msSinceLastResend < throttleLimit) {
      const secondsLeft = Math.ceil((throttleLimit - msSinceLastResend) / 1000);

      toastError(`Please wait ${secondsLeft} more second(s) before resending.`);

      return;
    }

    setLastResend(now);
    setIsSubmitting(true);
    initAmplify();

    try {
      await Auth.forgotPassword(email.toLowerCase());

      toastSuccess("Password reset code has been re-sent to your email");
    } catch (err) {
      const error = err as Error;

      toastError(
        error.message || "An error occurred during password reset request",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <form
        className="flex flex-col gap-1"
        onSubmit={
          codeSent
            ? handleSubmitReset(resetPassword)
            : handleSubmitEmail(sendResetCode)
        }
      >
        <Input
          type="email"
          label="Email"
          placeholder="me@example.com"
          status={errorsEmail.email ? "error" : "neutral"}
          errorMsg={errorsEmail.email?.message}
          {...registerEmail("email")}
          disabled={codeSent}
          msgPadded
          data-testid="email-input"
        />
        {codeSent && (
          <>
            <Input
              type="text"
              label="Verification Code"
              placeholder="Enter your code"
              status={errorsReset.verificationCode ? "error" : status}
              errorMsg={errorsReset.verificationCode?.message}
              msgPadded
              {...registerReset("verificationCode")}
            />
            <Input
              type={passwordVisible ? "text" : "password"}
              label="New Password"
              placeholder="••••••••••••••••"
              status={errorsReset.newPassword ? "error" : status}
              errorMsg={errorsReset.newPassword?.message}
              msgPadded
              {...registerReset("newPassword")}
              rightIcon={
                <Icon
                  className="cursor-pointer stroke-2"
                  name={passwordVisible ? "Eye" : "EyeSlash"}
                  onClick={() => setPasswordVisible(!passwordVisible)}
                />
              }
            />
            <Input
              type={passwordVisible ? "text" : "password"}
              label="Confirm Password"
              placeholder="••••••••••••••••"
              status={errorsReset.confirmPassword ? "error" : status}
              errorMsg={errorsReset.confirmPassword?.message}
              msgPadded
              {...registerReset("confirmPassword")}
              rightIcon={
                <Icon
                  className="cursor-pointer stroke-2"
                  name={passwordVisible ? "Eye" : "EyeSlash"}
                  onClick={() => setPasswordVisible(!passwordVisible)}
                />
              }
            />
          </>
        )}
        <input type="submit" className="hidden" />
      </form>
      <div className="flex flex-col gap-6">
        <div className="flex gap-2">
          <Button
            className="flex-1"
            variant="v2/stroke"
            type="button"
            onClick={handleCancel}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <Button
            variant="v2/filled"
            className="flex-1"
            loading={isSubmitting}
            disabled={isSubmitting}
            onClick={
              codeSent
                ? handleSubmitReset(resetPassword)
                : handleSubmitEmail(sendResetCode)
            }
            data-testid="send-code-button"
          >
            {codeSent ? "Reset Password" : "Send Code"}
          </Button>
        </div>
        {codeSent && (
          <div className="text-sm text-zinc-500">
            Didn't receive a code?{" "}
            <a
              onClick={resendCode}
              className="text-sm text-zinc-900 underline hover:underline"
              style={{ fontWeight: 450 }}
            >
              Resend
            </a>
          </div>
        )}
      </div>
    </>
  );
};
